import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { BusinessVendorIdentityInput } from "@releve/shared";
import { Plus, ChevronDown, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CountrySelect } from "@/components/ui/country-select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useCreateBusiness, useUpdateBusiness, Business } from "@/hooks/useBusinesses";
import { useBusinessVendorIdentity } from "@/hooks/useBusinessVendorIdentity";
import { useCurrentUserOrgRole } from "@/hooks/useCurrentUserOrgRole";
import { DualScopeSelect } from "@/components/common/DualScopeSelect";
import { AddressLookupInput } from "@/components/ui/address-lookup-input";
import { LegalFormSelect } from "@/components/common/LegalFormSelect";
import { getJurisdictionIdentityConfig } from "@/config/jurisdictionIdentity";
import { useOrgBasePath, useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useTranslation } from "@/hooks/useTranslation";
import { BusinessDangerZone } from "./BusinessDangerZone";

const createFormSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('businessDialog.validation.nameRequired')),
  code: z.string().optional(),
  contact_email: z.string().optional().refine((val) => !val || z.string().email().safeParse(val).success, {
    message: t('businessDialog.validation.invalidEmail'),
  }),
  contact_phone: z.string().optional(),
  business_type_id: z.string().min(1, t('businessDialog.validation.typeRequired')),
  address: z.string().optional(),
  // Vendor identity fields
  trading_name: z.string().optional(),
  country_code: z.string().optional(),
  legal_form_id: z.string().nullable().optional(),
  registration_number: z.string().optional(),
  vat_number: z.string().optional(),
  iban: z.string().optional(),
  bic: z.string().optional(),
  is_related_party: z.boolean().optional(),
});

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

/**
 * The fields the dialog reads from the business it edits — the full
 * `Business` row and a `business_index` row both satisfy it. `contact_email`
 * must be the RAW column (never the index's display_email fallback), or a
 * save would write the fallback into the business.
 */
export type EditableBusiness = Pick<
  Business,
  'id' | 'name' | 'code' | 'contact_email' | 'contact_phone' | 'business_type_id' | 'address'
>;

interface CreateBusinessDialogProps {
  trigger?: React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
  onBusinessCreated?: () => void;
  business?: EditableBusiness | null; // If provided, we're editing
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Show the Delete danger zone (edit mode only). Opt-in: only the business
   * detail page passes it — the quick-add / grid / contact-list mounts don't.
   */
  showDangerZone?: boolean;
  /** After a successful delete — the caller decides where to go (the dialog never navigates). */
  onDeleted?: () => void;
  /**
   * After a successful edit, with the server's resolved business — called
   * BEFORE the dialog closes, so a caller that re-routes (e.g. the detail page
   * following a code rename) can navigate with `replace` first.
   */
  onUpdated?: (updated: { code: string }) => void;
}

export function CreateBusinessDialog({
  trigger,
  onBusinessCreated,
  business,
  open: controlledOpen,
  onOpenChange,
  showDangerZone = false,
  onDeleted,
  onUpdated,
}: CreateBusinessDialogProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [legalTaxOpen, setLegalTaxOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const isEditing = !!business;

  const { selectedOrganization } = useOrganization();
  const orgBasePath = useOrgBasePath();
  const orgNavigate = useOrgNavigate();
  const createBusinessMutation = useCreateBusiness();
  const updateBusinessMutation = useUpdateBusiness();
  // Vendor bank identity is accounting data (org roles step 3): a role without
  // `accounting` neither reads it (RLS returns nothing) nor sees / sends it.
  const { can } = useCurrentUserOrgRole();
  const showVendorIdentity = can("accounting");
  const { data: existingVendorIdentity } = useBusinessVendorIdentity(showVendorIdentity ? business?.id : undefined);

  const formSchema = React.useMemo(() => createFormSchema(t), [t]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      name: business?.name || "",
      code: business?.code || "",
      contact_email: business?.contact_email || "",
      contact_phone: business?.contact_phone || "",
      business_type_id: business?.business_type_id || "",
      address: business?.address || "",
      trading_name: "",
      country_code: "",
      legal_form_id: null,
      registration_number: "",
      vat_number: "",
      iban: "",
      bic: "",
      is_related_party: false,
    },
  });

  const watchedCountryCode = form.watch("country_code");
  const jurisdictionConfig = getJurisdictionIdentityConfig(watchedCountryCode || null);

  // Reset form when business prop or vendor identity changes
  React.useEffect(() => {
    if (business) {
      form.reset({
        name: business.name,
        code: business.code,
        contact_email: business.contact_email || "",
        contact_phone: business.contact_phone || "",
        business_type_id: business.business_type_id || "",
        address: business.address || "",
        trading_name: existingVendorIdentity?.trading_name ?? "",
        country_code: existingVendorIdentity?.country_code ?? "",
        legal_form_id: existingVendorIdentity?.legal_form_id ?? null,
        registration_number: existingVendorIdentity?.registration_number ?? "",
        vat_number: existingVendorIdentity?.vat_number ?? "",
        iban: existingVendorIdentity?.iban ?? "",
        bic: existingVendorIdentity?.bic ?? "",
        is_related_party: existingVendorIdentity?.is_related_party ?? false,
      });
      // Auto-expand legal/tax section if vendor identity data exists
      if (existingVendorIdentity) {
        setLegalTaxOpen(true);
      }
    } else {
      form.reset({
        name: "",
        code: "",
        contact_email: "",
        contact_phone: "",
        business_type_id: "",
        address: "",
        trading_name: "",
        country_code: "",
        legal_form_id: null,
        registration_number: "",
        vat_number: "",
        iban: "",
        bic: "",
        is_related_party: false,
      });
    }
  }, [business, existingVendorIdentity, form]);

  // Reset form when dialog closes in create mode
  React.useEffect(() => {
    if (!open && !isEditing) {
      form.reset();
      setLegalTaxOpen(false);
    }
  }, [open, isEditing, form]);

  // Check if any vendor identity fields have values
  const hasVendorIdentityData = (data: FormData) => {
    return !!(
      data.trading_name ||
      data.country_code ||
      data.legal_form_id ||
      data.registration_number ||
      data.vat_number ||
      data.iban ||
      data.bic ||
      data.is_related_party
    );
  };

  const vendorIdentityPayload = (data: FormData): BusinessVendorIdentityInput => ({
    trading_name: data.trading_name || null,
    country_code: data.country_code || null,
    legal_form_id: data.legal_form_id || null,
    registration_number: data.registration_number || null,
    vat_number: data.vat_number || null,
    iban: data.iban || null,
    bic: data.bic || null,
    is_related_party: data.is_related_party ?? false,
  });

  // One request per save: the vendor identity rides in the same create /
  // update call (`create_business` / `update_business` upsert it in the same
  // transaction). Empty strings are sent as-is — the shared schema folds them
  // to NULL, so clearing an input clears the column. The mutation hooks toast
  // success and failure (coded 409s included); the MutationCache reports.
  const onSubmit = (data: FormData) => {
    if (!selectedOrganization) return;

    const fields = {
      name: data.name,
      contact_email: data.contact_email ?? "",
      contact_phone: data.contact_phone ?? "",
      business_type_id: data.business_type_id,
      address: data.address ?? "",
    };

    if (isEditing && business) {
      const code = data.code?.trim();
      updateBusinessMutation.mutate(
        {
          id: business.id,
          data: {
            ...fields,
            // `code` is NOT NULL: a blanked input leaves it unchanged.
            ...(code ? { code } : {}),
            // Send the identity when there is one to write OR one to clear.
            ...(showVendorIdentity && (hasVendorIdentityData(data) || existingVendorIdentity)
              ? { vendor_identity: vendorIdentityPayload(data) }
              : {}),
          },
        },
        {
          onSuccess: (updated) => {
            onUpdated?.(updated);
            setOpen(false);
            onBusinessCreated?.();
          },
        }
      );
    } else {
      createBusinessMutation.mutate(
        {
          organizationId: selectedOrganization.id,
          data: {
            ...fields,
            // Blank → the server generates VEN-###.
            code: data.code?.trim() || null,
            ...(showVendorIdentity && hasVendorIdentityData(data) ? { vendor_identity: vendorIdentityPayload(data) } : {}),
          },
        },
        {
          onSuccess: (createdBusiness) => {
            form.reset();
            // Continue to the new business's detail page (the server's code).
            // Navigate with `replace` so it supersedes the dialog's history
            // entry — FullscreenFormDialog then skips its own history.back() on
            // close (no race, no orphan). Navigate first, then close.
            orgNavigate(`/resources/businesses/${createdBusiness.code}`, { replace: true });
            setOpen(false);
            onBusinessCreated?.();
          },
        }
      );
    }
  };

  const isPending = createBusinessMutation.isPending || updateBusinessMutation.isPending;
  const isSubmitDisabled = !form.formState.isValid || (isEditing && !form.formState.isDirty);

  return (
    <>
      {controlledOpen === undefined && (
        trigger
          ? React.cloneElement(trigger, {
              onClick: (e: React.MouseEvent) => {
                trigger.props.onClick?.(e);
                setOpen(true);
              }
            })
          : (
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('businessDialog.newBusiness')}
            </Button>
          )
      )}

      <FullscreenFormDialog
        open={open}
        onOpenChange={setOpen}
        title={isEditing ? t('businessDialog.editTitle') : t('businessDialog.createTitle')}
        description={isEditing ? t('businessDialog.editDescription') : t('businessDialog.createDescription')}
        onSubmit={form.handleSubmit(onSubmit)}
        submitLabel={isEditing ? t('businessDialog.updateButton') : t('businessDialog.createButton')}
        isSubmitting={isPending}
        submitDisabled={isSubmitDisabled}
        formId="business-form"
        skipCloseConfirmation={!form.formState.isDirty}
      >
        <Form {...form}>
          <div className="space-y-8">
            {/* Business content card — wraps the document body */}
            <Card>
              <CardContent className="pt-6 space-y-8">
                <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                  {t('businessDialog.heroTitle')}
                </h2>

                {/* SECTION: Details */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {t('businessDialog.sections.details')}
                  </h3>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel>{t('businessDialog.fields.name')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Input placeholder={t('businessDialog.placeholders.name')} {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('businessDialog.fields.code')}</FormLabel>
                            <FormControl>
                              <Input placeholder={t('businessDialog.placeholders.code')} {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="business_type_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t('businessDialog.fields.type')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DualScopeSelect
                                tableName="business_types"
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder={t('businessDialog.placeholders.type')}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Full-bleed colored section: Contact. Theme-aware `bg-muted` (not the
                    invoice's frozen paper) because the inputs render theme-aware text. */}
                <div className="-mx-6 px-6 py-6 bg-muted">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {t('businessDialog.sections.contact')}
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="contact_email"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t('businessDialog.fields.email')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <Input type="email" placeholder={t('businessDialog.placeholders.email')} {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="contact_phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('businessDialog.fields.phone')}</FormLabel>
                            <FormControl>
                              <PhoneInput
                                value={field.value}
                                onChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel>{t('businessDialog.fields.address')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <AddressLookupInput
                              value={field.value || ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal & Tax Details (collapsible) — outside the document card.
                Accounting roles only (vendor bank identity). */}
            {showVendorIdentity && (
            <Collapsible open={legalTaxOpen} onOpenChange={setLegalTaxOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" type="button" className="w-full justify-between px-0 hover:bg-transparent hover:text-foreground">
                  <span className="text-sm font-medium">{t('businessDialog.legalTaxDetails')}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${legalTaxOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="trading_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('businessIdentity.tradingName')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('businessDialog.placeholders.tradingName')} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="country_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('businessDialog.fields.country')}</FormLabel>
                        <FormControl>
                          <CountrySelect
                            value={field.value || ""}
                            onValueChange={field.onChange}
                            placeholder={t('businessDialog.placeholders.country')}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('businessDialog.fields.legalForm')}</Label>
                    <LegalFormSelect
                      countryCode={watchedCountryCode || null}
                      value={form.watch("legal_form_id")}
                      onValueChange={(value) => form.setValue("legal_form_id", value, { shouldDirty: true })}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="registration_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{jurisdictionConfig.registrationNumber.label}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={jurisdictionConfig.registrationNumber.format ?? t('businessDialog.placeholders.registrationNumber')}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="vat_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{jurisdictionConfig.vatNumber.label}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={jurisdictionConfig.vatNumber.format ?? t('businessDialog.placeholders.vatNumber')}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="iban"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('businessIdentity.iban')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('businessDialog.placeholders.iban')} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="bic"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('businessDialog.fields.bic')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('businessDialog.placeholders.bic')} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex items-end pb-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="is-related-party"
                        checked={form.watch("is_related_party") ?? false}
                        onCheckedChange={(checked) => form.setValue("is_related_party", checked === true, { shouldDirty: true })}
                      />
                      <Label htmlFor="is-related-party">{t('businessIdentity.relatedParty')}</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-sm mb-2">
                              {t('businessDialog.relatedPartyTooltip')}
                            </p>
                            <Link
                              to={`${orgBasePath}/help/accounting/related-party-transactions`}
                              className="text-sm text-primary hover:underline"
                            >
                              {t('learnMore')} →
                            </Link>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
            )}

            {/* Danger zone — edit mode only, opt-in, after the document. */}
            {showDangerZone && isEditing && business && open && (
              <BusinessDangerZone
                business={business}
                disabled={isPending}
                onDeleted={() => {
                  onDeleted?.();
                  setOpen(false);
                }}
              />
            )}
          </div>
        </Form>
      </FullscreenFormDialog>
    </>
  );
}
